
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Key, Shield, Settings } from "lucide-react";
import { AgentConfiguration } from "@/components/candidates/types/agent";

type ApiKeyManagementTabProps = {
  selectedAgent: AgentConfiguration;
  updateAgentConfig: (config: Partial<AgentConfiguration>) => void;
};

export const ApiKeyManagementTab = ({ selectedAgent, updateAgentConfig }: ApiKeyManagementTabProps) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            API Key Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground mb-4">
            Configure API key preferences for this agent. You can use system keys, organization keys, or user-specific keys.
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-blue-500" />
                  <span className="font-medium">System Keys</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Use platform-managed API keys (default)
                </p>
                <Badge variant="outline" className="text-xs">
                  Fallback Enabled
                </Badge>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Settings className="h-4 w-4 text-green-500" />
                  <span className="font-medium">Organization Keys</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Use organization-specific API keys
                </p>
                <Badge variant="secondary" className="text-xs">
                  Not Configured
                </Badge>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Key className="h-4 w-4 text-purple-500" />
                  <span className="font-medium">User Keys</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Use user-specific API keys
                </p>
                <Badge variant="secondary" className="text-xs">
                  Not Configured
                </Badge>
              </Card>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Required API Keys for this Agent</h4>
              <div className="space-y-3">
                {selectedAgent.model && (
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <span className="font-medium">{selectedAgent.model.provider.toUpperCase()} API Key</span>
                      <p className="text-sm text-muted-foreground">Required for {selectedAgent.model.name}</p>
                    </div>
                    <Badge variant="outline">System Managed</Badge>
                  </div>
                )}
                
                {selectedAgent.tools && selectedAgent.tools.length > 0 && (
                  selectedAgent.tools
                    .filter(tool => tool.requiredCredentials && tool.requiredCredentials.length > 0)
                    .map(tool => (
                      <div key={tool.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <span className="font-medium">{tool.name}</span>
                          <p className="text-sm text-muted-foreground">
                            Requires: {tool.requiredCredentials?.join(', ')}
                          </p>
                        </div>
                        <Badge variant="secondary">Not Configured</Badge>
                      </div>
                    ))
                )}
              </div>
            </div>

            <div className="border-t pt-4">
              <Button variant="outline" className="w-full">
                <Settings className="mr-2 h-4 w-4" />
                Configure API Keys
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                API key management will be available in a future update
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
